import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import { t } from 'ember-intl';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import StatePill from 'tio-employee/components/tas/state-pill';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type { DataSourceInfo, LiveTasData } from 'tio-employee/services/reimagined-data';

interface TasProgramDetailsModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
  programId: string;
}

interface TasProgramDetailsSignature {
  Args: {
    model: TasProgramDetailsModel;
  };
}

export default class CareerPathwaysTasProgramDetails extends Component<TasProgramDetailsSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get program() {
    return this.args.model.tasData.programs.find(
      (program) => program.id === this.args.model.programId
    );
  }

  <template>
    <VStack class="gap-6">
      <div class="tio-col-tight">
        <Heading @level={{1}}>{{t "common.details"}}</Heading>
        <Text @variant="muted">{{t "common.programs"}}</Text>
      </div>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-program-details"
      >
        <Section>
          <:header>
            <Heading @level={{2}}>{{t "common.details"}}</Heading>
          </:header>
          <:body>
            {{#if this.program}}
              <div class="tio-row-start-between gap-4">
                <div>
                  <Heading @level={{3}}>{{this.program.name}}</Heading>
                  {{#if this.program.institution}}
                    <Text @variant="muted">{{this.program.institution}}</Text>
                  {{/if}}
                  {{#if this.program.major}}
                    <Text @variant="muted">
                      {{t
                        "tuition_assistance.reimagined.dashboard.label_major"
                        major=this.program.major
                      }}
                    </Text>
                  {{/if}}
                  {{#if this.program.startDate}}
                    <Text @variant="muted">
                      {{t
                        "tuition_assistance.reimagined.programs.label_starts"
                        date=this.program.startDate
                      }}
                    </Text>
                  {{/if}}
                </div>
                <StatePill @state={{this.program.state}} />
              </div>
              <div class="mt-4">
                <LinkTo @route="authenticated.tas.programs.details" @model={{@model.programId}}>
                  <Button @intent="primary">{{t "common.view"}}</Button>
                </LinkTo>
              </div>
            {{else}}
              <Text @variant="muted">{{t
                  "tuition_assistance.reimagined.programs.empty_body"
                }}</Text>
            {{/if}}
          </:body>
        </Section>
      </DataIndicator>
    </VStack>
  </template>
}
